import ConsignmentEntry from 'data/ConsignmentEntry';
import Order from 'data/Order';
import Warehouse from 'data/Warehouse';
import Vendor from 'data/Vendor';
import ShippingCarrier from 'data/ShippingCarrier';
import DeliveryAddress from 'data/DeliveryAddress';
import ConsignmentShortPayments from 'data/ConsignmentShortPayments';
import BusinessUnitCompany from './BusinessUnitCompany';
import ConsignmentCredit from './ConsignmentCredit';
import DowJonesResult from './DowJonesResult';
import PickupContact from './PickupContact';
import { PRICE_TYPE } from 'constants/product';
import ConsignmentModificationEntry from './ConsignmentModificationEntry';
import ConsignmentHistoryChange from './ConsignmentHistoryChange';
import Ticket from './Ticket';
import { BusinessUnit } from './BusinessUnit';

export default interface Consignment {
  id: number;
  consignmentId: string;
  orderId: string;
  state: string;
  consignmentEntries: ConsignmentEntry[];
  consignmentShortPayments: ConsignmentShortPayments[];
  order: Pick<Order, 'orderId' | 'customer' | 'paymentType'>;
  warehouse: Pick<Warehouse, 'id' | 'name' | 'code'>;
  deliveryType: string;
  trackingId: string;
  referenceNumber: string;
  availableDate: string;
  dispatchDate: string;
  deliveryCarrier: ShippingCarrier;
  totalPrice: number;
  totalPriceWithTax: number;
  created: string;
  updatedBy: string;
  dowJonesStatus?: string;
  vendorId?: number;
  totalTax: number;
  totalDeliveryCost: number;
  totalDiscounts?: number;
  vendor: Vendor;
  cancelationReason?: any;
  totalSurcharges: number;
  dowJones: DowJonesResult;
  salesOrderNumber?: string;
  countryId: number;
  businessUnitId?: number;
  businessUnit?: Pick<BusinessUnit, 'id' | 'name' | 'code'>;
  modificationEntries?: ConsignmentModificationEntry[];
  consignmentHistoryChanges?: ConsignmentHistoryChange[];
  consignmentType: string;
  deliveryAddress: Pick<DeliveryAddress, 'id' | 'firstName' | 'lastName' | 'addressLine1' | 'addressLine2' | 'town' | 'postalCode' | 'countryIsoCode' | 'state'>;
  pickupContact: PickupContact;
  pointOfServiceLocation: string;
  paymentStatus: string;
};

export interface ConsignmentDetails {
  id: number;
  consignmentId: string;
  orderId: string;
  consignmentDate: string;
  state: string;
  previousState: string;
  deliveryType: string;
  trackingId: string;
  referenceNumber: string;
  consignmentCount: number;
  consignmentCredit: ConsignmentCredit;
  consignmentEntries: ConsignmentEntry[];
  consignmentEntriesAddons: ConsignmentEntry[];
  consignmentSnapshotEntries: ConsignmentSnapshotEntry[];
  /** Complete pre-edit item list from the EDIT_ORDER snapshot — only when an edit session exists */
  editOrderSnapshotEntries?: ConsignmentSnapshotEntry[];
  consignmentShortPayments: ConsignmentShortPayments[];
  order: Pick<Order, 'id' | 'orderId' | 'customer' | 'paymentType' | 'cancelEmailSentDate' | 'companyId' | 'company' | 'companyName'>;
  warehouse: Pick<Warehouse, 'id' | 'name' | 'code' | 'sapPlantId'>;
  shippingCarrier: ShippingCarrier;
  availableDate: string;
  created: string;
  updated: string;
  dispatchDate: string;
  pickedUpBy: string;
  totalTax: number;
  totalDeliveryCost: number;
  totalPrice: number;
  totalPriceWithTax: number;
  subTotal: number;
  deliveryAddress: Pick<DeliveryAddress, 'id' | 'firstName' | 'lastName' | 'addressLine1' | 'addressLine2' | 'town' | 'postalCode' | 'countryIsoCode' | 'state'>;
  businessUnitCompanyId?: string;
  readOnly?: boolean;
  cancellationReason?: string;
  vendorId: string;
  isSlbVendor:boolean;
  businessUnitCompany?: BusinessUnitCompany;
  totalSurcharges: number;
  totalAdditionalShippingCost: number;
  totalDiscounts?: number;
  paymentStatus: string;
  cancelationReason?: any;
  salesOrderNumber?: string;
  countryId: number;
  pickupContact: PickupContact;
  syncToSap?:string;
  consignmentType: string;
  rentalStartDate: string;
  rentalEndDate: string;
  subscriptionStartDate?: string;
  subscriptionEndDate?: string;
  subscriptionPeriod?: number;
  modificationEntry: ConsignmentModificationEntry;
  consignmentHistoryChanges?: ConsignmentHistoryChange[];
  oldRentalStartDate?: string;
  oldRentalEndDate?: string;
  tickets?: Ticket[];
  weekdaysOnly: boolean;
  consignmentSnapshot?: ConsignmentSnapshot;
  /** Edit order session status (PENDING / SUCCESS / FAILED) — null when no session exists */
  editOrderResult?: string | null;
  /** Order total captured before the edit session — null when no edit session exists */
  editOrderOriginalTotal?: number | null;
  /** True right after an approval on the buyer's behalf, until Hybris's reply has renumbered the entries */
  isIncorporatingEditChanges?: boolean;
  modificationEntries?: ConsignmentModificationEntry[]
  vendor: Vendor;
  salesOrderStatus?: {
    id: number;
    consignmentId: string;
    salesOrderNumber?: string;
    status: string;
    failureCase?: string;
    requestPayload?: string;
    responsePayload?: string;
    errorMessage?: string;
    httpStatusCode?: number;
    createdDate?: string;
    correlationId?: string;
    actionType?: string;
    salesOrderType?: string;
    // Transformed fields from backend
    transformedMessage?: string;
  };
  isStockManual?: boolean;
  paymentType?: string;
  creditSource?: string;
  consignmentAutomationFlags?: ConsignmentAutomationFlag[];
  businessUnit?: BusinessUnit;
  businessUnitId?: number;
  availableForRenewal?: boolean;
  cognitoFormId?: string;
  cognitoEntryId?: string;
  isAffiliateFormPdfGenerated?: boolean | null;
  cancelationRecord?: {
    id: number;
    cancelationMessage?: string;
    canceledBy: string;
    reasonCode: string;
    canceledFrom: string;
  };
  hasCognitoForm?: boolean;
  affiliateFormSubmittedOn?: string;
};

// First define the ConsignmentSnapshotEntry interface
export interface ConsignmentSnapshotEntry extends Omit<ConsignmentEntry, 'id'> {
  id: number;
  consignmentEntryId: number;
  /** The vendor's comment on the line before the edit (edit-order snapshot only) */
  comment?: string;
}

// Then extend ConsignmentDetails to create ConsignmentSnapshot
export interface ConsignmentSnapshot extends Omit<ConsignmentDetails, 
  'consignmentEntries' | 
  'consignmentEntriesAddons' | 
  'consignmentSnapshotEntries' | 
  'consignmentSnapshot' >  {
  
  consignmentTableId: number; // New field
  // Override the arrays to use the new entry type
  consignmentEntries: ConsignmentSnapshotEntry[];
  consignmentEntriesAddons: ConsignmentSnapshotEntry[];
  consignmentSnapshotEntries: ConsignmentSnapshotEntry[];
}

export interface ConsignmentsList {
  page: number;
  totalPages: number;
  size: number;
  totalItems: number;
  consignments: Consignment[];
};

export interface ConsignmentRentDates {
  rentalStartDate: string;
  rentalEndDate: string;
  newRentalStartDate?: string;
  newRentalEndDate?: string;
}

export interface ShippingConsignmentRequest {
  shippingCarrier: ShippingCarrier;
  trackingId: string;
  deliveryDate?: string;
  attachDocuments: boolean;
  rentalNewStartDate?: string;
  rentalNewEndDate?: string;
};

export interface AdditionalShippingCostRequest {
  shippingCarrier?: ShippingCarrier;
  deliveryDate?: string;
  shippingAdditionalInfo?: string;
  totalAdditionalShippingCost: string;
};

export interface SplitConsignmentShippingCostRequest {
  state: string;
  additionalShippingCost: number;
  estimatedAvailableDate?: string;
  rentalStartDate?: number;
  rentalEndDate?: number;
  entryNumbers?: number[];
};

export interface PickupConsignmentRequest {
  warehouseId: number;
  referenceNumber: string;
  pickupDate?: string;
  attachDocuments: boolean;
  rentalNewStartDate?: string;
  rentalNewEndDate?: string;
};

export interface DispatchRequest {
  dispatchDate: string;
  pickedUpBy: string;
};

export interface ConsignmentAutomationFlag {
  productVariantCode: string;
  materialApiValidation: boolean;
  stocksApiValidation: boolean;
}

export const emptyConsignmentDetails: ConsignmentDetails = {
  id: 0,
  vendorId: '',
  consignmentId: '',
  created: '',
  updated: '',
  orderId: '',
  consignmentDate: '',
  consignmentCount: 0,
  deliveryType: '',
  state: '',
  previousState: '',
  trackingId: '',
  referenceNumber: '',
  modificationEntry: {
    id: 0,
    sequenceNumber: 0,
    consignmentId: '',
    originalStartDate: '',
    originalEndDate: '',
    newStartDate: '',
    newEndDate: '',
    requestedByEmail: '',
    requestedBy: '',
    status: '',
    message: '',
    initiatedFrom: '',
    originalDeliveryDate: '',
    newDeliveryDate: ''
  },
  consignmentEntries: [],
  consignmentEntriesAddons: [],
  consignmentSnapshotEntries: [],
  consignmentCredit: {
    id: 0,
    vendorCompanyId: '',
    vendorCompanyCreditId: '',
    orderId: '',
    consignmentId: '',
    totalCreditLimit: 0,
    availableCreditLimit: 0,
    overrideAmount: 0,
    createdBy: '',
    status: '',
    vendorCompanyCredit: {
      id: 0,
      availableCreditLimit: 0,
      currency: '',
      limitLastModified: '',
      readonly: false,
      riskClass: '',
      riskClassDescription: '',
      riskClassLastModified: '',
      segmentId: '',
      totalCreditLimit: 0,
      vendorCompanyId: ''
    }
  },
  consignmentShortPayments: [],
  order: {
    id: 0,
    orderId: '',
    paymentType: '',
    companyName: '',
    customer: {
      id: 0,
      customerId: '',
      firstName: '',
      lastName: '',
      fullName: '',
      email: '',
      phone: '',
      companyName: ''
    }
  },
  warehouse: {
    id: 0,
    name: '',
    code: '',
    sapPlantId: ''
  },
  shippingCarrier: {
    id: 0,
    name: ''
  },
  cancelationReason: '',
  availableDate: '',
  dispatchDate: '',
  pickedUpBy: '',
  totalTax: 0,
  totalDeliveryCost: 0,
  totalPrice: 0,
  totalPriceWithTax: 0,
  totalDiscounts: 0,
  subTotal: 0,
  totalSurcharges: 0,
  totalAdditionalShippingCost: 0,
  paymentStatus: '',
  deliveryAddress: {
    id: 0,
    firstName: '',
    lastName: '',
    addressLine1: '',
    addressLine2: '',
    town: '',
    postalCode: '',
    countryIsoCode: '',
    state: ''
  },
  salesOrderNumber:'',
  countryId: 0,
  isSlbVendor: false,
  pickupContact: {
    id: 0,
    firstName: '',
    lastName: '',
    email: '',
    phone: ''
  },
  consignmentType: PRICE_TYPE.PURCHASE,
  rentalEndDate: '',
  rentalStartDate: '',
  subscriptionStartDate: '',
  subscriptionEndDate: '',
  subscriptionPeriod: 0,
  weekdaysOnly: false,
  vendor: {
    id: 0,
    name: '',
    code: '',
    media: [],
    brands: [],
    warehouses: [],
    attributes: [],
  },
  hasCognitoForm: false,
  affiliateFormSubmittedOn: ''
};

// Create an empty snapshot object by extending emptyConsignmentDetails
export const emptyConsignmentSnapshot: ConsignmentSnapshot = {
  ...emptyConsignmentDetails,
  consignmentTableId: 0,
  consignmentEntries: [],
  consignmentEntriesAddons: [],
  consignmentSnapshotEntries: []
};

export type ConsignmentKeys = keyof Consignment;
